import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { adminService } from '../services/adminService';
import ConfirmDialog from '../components/ConfirmDialog';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const [statistics, setStatistics] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, onConfirm: null, title: '', message: '', type: 'warning' });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'users', 'connections'
  const [userFilters, setUserFilters] = useState({
    role: '',
    search: '',
    page: 1,
    limit: 20,
  });
  const [userPagination, setUserPagination] = useState({
    total: 0,
    totalPages: 0,
  });
  const [editingUser, setEditingUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  
  // États pour les statistiques de connexions
  const [connectionStats, setConnectionStats] = useState(null);
  const [connectionLoading, setConnectionLoading] = useState(false);
  const [connectionFilters, setConnectionFilters] = useState({
    period: 'day',
    startDate: '',
    endDate: '',
    role: '',
  });

  useEffect(() => {
    // Attendre que le user soit chargé
    if (user === null) {
      return; // User pas encore chargé, attendre
    }
    
    if (!user || user.role !== 'admin') {
      console.log('AdminDashboard: Redirection - user:', user?.role);
      navigate('/dashboard');
      return;
    }
    
    console.log('AdminDashboard: Utilisateur admin détecté, chargement des statistiques...');
    fetchStatistics();
  }, [user]);

  useEffect(() => {
    if (activeTab === 'users' && user && user.role === 'admin') {
      fetchUsers();
    }
  }, [activeTab, userFilters]);

  useEffect(() => {
    if (activeTab === 'connections' && user && user.role === 'admin') {
      fetchConnectionStatistics();
    }
  }, [activeTab, connectionFilters.period, connectionFilters.startDate, connectionFilters.endDate, connectionFilters.role]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      console.log('AdminDashboard: Appel de adminService.getStatistics()...');
      const response = await adminService.getStatistics();
      console.log('AdminDashboard: Réponse complète:', JSON.stringify(response, null, 2));
      if (response.success && response.statistics) {
        console.log('AdminDashboard: Statistiques chargées avec succès');
        setStatistics(response.statistics);
      } else {
        console.error('AdminDashboard: Erreur lors du chargement:', response.error);
        // Ne pas bloquer l'affichage, juste logger
      }
    } catch (error) {
      console.error('AdminDashboard: Exception lors du chargement:', error);
      // Ne pas bloquer l'affichage, juste logger
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await adminService.getAllUsers(userFilters);
      if (response.success) {
        setUsers(response.users || []);
        setUserPagination({
          total: response.total || 0,
          totalPages: response.totalPages || 0,
        });
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleUserFilterChange = (filterType, value) => {
    setUserFilters(prev => ({
      ...prev,
      [filterType]: value,
      page: 1, // Reset à la page 1
    }));
  };

  const handleUpdateRole = async (userId, role) => {
    try {
      const response = await adminService.updateUserRole(userId, role);
      if (response.success) {
        toast.success('Rôle mis à jour avec succès');
        setEditingUser(null);
        fetchUsers();
      } else {
        toast.error(`Erreur: ${response.error}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Une erreur est survenue');
    }
  };

  const handleToggleUserStatus = async (userId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Changer le statut',
      message: 'Êtes-vous sûr de vouloir changer le statut de cet utilisateur ?',
      type: 'warning',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        try {
          const response = await adminService.toggleUserStatus(userId);
          if (response.success) {
            toast.success('Statut mis à jour avec succès');
            fetchUsers();
          } else {
            toast.error(`Erreur: ${response.error}`);
          }
        } catch (error) {
          console.error('Erreur:', error);
          toast.error('Une erreur est survenue');
        }
      },
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const fetchConnectionStatistics = async () => {
    try {
      setConnectionLoading(true);
      const response = await adminService.getConnectionStatistics(
        connectionFilters.period,
        connectionFilters.startDate || null,
        connectionFilters.endDate || null,
        connectionFilters.role || null
      );
      if (response.success && response.data) {
        setConnectionStats(response.data);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques de connexions:', error);
      toast.error('Erreur lors du chargement des statistiques de connexions');
    } finally {
      setConnectionLoading(false);
    }
  };

  const handleConnectionFilterChange = (filterType, value) => {
    setConnectionFilters(prev => ({
      ...prev,
      [filterType]: value,
    }));
  };

  const handleExportReport = async () => {
    try {
      toast.success('Export du rapport en cours...');
      const response = await adminService.exportConnectionReport(
        connectionFilters.period,
        connectionFilters.startDate || null,
        connectionFilters.endDate || null,
        connectionFilters.role || null,
        'csv'
      );
      if (response.success) {
        toast.success('Rapport exporté avec succès');
      } else {
        toast.error(response.error || 'Erreur lors de l\'export');
      }
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      toast.error('Erreur lors de l\'export du rapport');
    }
  };

  const formatDuration = (milliseconds) => {
    if (!milliseconds) return 'N/A';
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  };

  // Vérifier si l'utilisateur est chargé et est admin
  if (!user) {
    return (
      <div>
        <Navbar />
        <div className="container">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (user.role !== 'admin') {
    return null; // La redirection est gérée dans useEffect
  }

  return (
    <div>
      <Navbar />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        onConfirm={() => {
          if (confirmDialog.onConfirm) {
            confirmDialog.onConfirm();
          }
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        }}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      <div className="container">
        <div style={{ margin: '40px auto', maxWidth: '1400px' }}>
          <h1 style={{ marginBottom: '30px', color: '#FF0000', fontSize: '32px', fontWeight: '700' }}>
            👑 Dashboard Administrateur
          </h1>

          {/* Onglets */}
          <div style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '30px',
            borderBottom: '2px solid #e0e0e0',
            flexWrap: 'wrap',
          }}>
            <button
              onClick={() => setActiveTab('overview')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: 'none',
                borderBottom: activeTab === 'overview' ? '3px solid #FF0000' : '3px solid transparent',
                color: activeTab === 'overview' ? '#FF0000' : '#666',
                fontWeight: activeTab === 'overview' ? 'bold' : 'normal',
                cursor: 'pointer',
                fontSize: '16px',
              }}
            >
              📊 Vue d'ensemble
            </button>
            <button
              onClick={() => setActiveTab('users')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: 'none',
                borderBottom: activeTab === 'users' ? '3px solid #FF0000' : '3px solid transparent',
                color: activeTab === 'users' ? '#FF0000' : '#666',
                fontWeight: activeTab === 'users' ? 'bold' : 'normal',
                cursor: 'pointer',
                fontSize: '16px',
              }}
            >
              👥 Gestion des utilisateurs
            </button>
            <button
              onClick={() => setActiveTab('connections')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: 'none',
                borderBottom: activeTab === 'connections' ? '3px solid #FF0000' : '3px solid transparent',
                color: activeTab === 'connections' ? '#FF0000' : '#666',
                fontWeight: activeTab === 'connections' ? 'bold' : 'normal',
                cursor: 'pointer',
                fontSize: '16px',
              }}
            >
              🔌 Statistiques de connexions
            </button>
            <button
              onClick={() => navigate('/admin/advertisements')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: 'none',
                borderBottom: '3px solid transparent',
                color: '#666',
                fontWeight: 'normal',
                cursor: 'pointer',
                fontSize: '16px',
              }}
            >
              📢 Gestion des publicités
            </button>
          </div>

          {/* Vue d'ensemble */}
          {activeTab === 'overview' && (
            <>
              {loading ? (
                <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ color: '#666', marginBottom: '20px' }}>
                    Chargement des statistiques...
                  </p>
                </div>
              ) : !statistics ? (
                <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ color: '#dc3545', marginBottom: '20px' }}>
                    Impossible de charger les statistiques
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={fetchStatistics}
                  >
                    Réessayer
                  </button>
                </div>
              ) : (
            <div>
              {/* Cartes de statistiques principales */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '20px',
                marginBottom: '40px',
              }}>
                <div className="card" style={{ textAlign: 'center', padding: '25px', backgroundColor: '#f8f9fa' }}>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#FF0000', marginBottom: '10px' }}>
                    {statistics?.orders?.total || 0}
                  </div>
                  <div style={{ color: '#666', fontSize: '16px' }}>Total Commandes</div>
                </div>

                <div className="card" style={{ textAlign: 'center', padding: '25px', backgroundColor: '#d4edda' }}>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#155724', marginBottom: '10px' }}>
                    {formatCurrency(statistics?.revenue?.total || 0)}
                  </div>
                  <div style={{ color: '#666', fontSize: '16px' }}>Revenus Totaux</div>
                </div>

                <div className="card" style={{ textAlign: 'center', padding: '25px', backgroundColor: '#d1ecf1' }}>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#0c5460', marginBottom: '10px' }}>
                    {formatCurrency(statistics?.revenue?.commission || 0)}
                  </div>
                  <div style={{ color: '#666', fontSize: '16px' }}>Commissions</div>
                </div>

                <div className="card" style={{ textAlign: 'center', padding: '25px', backgroundColor: '#fff3cd' }}>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#856404', marginBottom: '10px' }}>
                    {statistics?.users?.total || 0}
                  </div>
                  <div style={{ color: '#666', fontSize: '16px' }}>Total Utilisateurs</div>
                </div>

                <div className="card" style={{ textAlign: 'center', padding: '25px', backgroundColor: '#d1ecf1' }}>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#0c5460', marginBottom: '10px' }}>
                    {statistics?.users?.active || 0}
                  </div>
                  <div style={{ color: '#666', fontSize: '16px' }}>Utilisateurs Actifs</div>
                </div>

                <div className="card" style={{ textAlign: 'center', padding: '25px', backgroundColor: '#f8d7da' }}>
                  <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#721c24', marginBottom: '10px' }}>
                    {statistics?.orders?.recent || 0}
                  </div>
                  <div style={{ color: '#666', fontSize: '16px' }}>Commandes (30j)</div>
                </div>
              </div>

              {/* Statistiques détaillées */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '20px', marginBottom: '40px' }}>
                {/* Commandes par statut */}
                <div className="card" style={{ padding: '25px' }}>
                  <h3 style={{ marginBottom: '20px', color: '#333', fontSize: '20px' }}>📦 Commandes par statut</h3>
                  <div style={{ display: 'grid', gap: '15px' }}>
                    {statistics?.orders?.byStatus && Object.keys(statistics.orders.byStatus).length > 0 ? (
                      Object.entries(statistics.orders.byStatus).map(([status, count]) => (
                        <div key={status} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#666' }}>{status}:</span>
                          <strong style={{ color: '#FF0000', fontSize: '18px' }}>{count}</strong>
                        </div>
                      ))
                    ) : (
                      <p style={{ color: '#666', textAlign: 'center' }}>Aucune donnée disponible</p>
                    )}
                  </div>
                </div>

                {/* Commandes par plateforme */}
                <div className="card" style={{ padding: '25px' }}>
                  <h3 style={{ marginBottom: '20px', color: '#333', fontSize: '20px' }}>🛒 Commandes par plateforme</h3>
                  <div style={{ display: 'grid', gap: '15px' }}>
                    {statistics?.orders?.byPlatform && Object.keys(statistics.orders.byPlatform).length > 0 ? (
                      Object.entries(statistics.orders.byPlatform).map(([platform, count]) => (
                        <div key={platform} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#666' }}>{platform}:</span>
                          <strong style={{ color: '#FF0000', fontSize: '18px' }}>{count}</strong>
                        </div>
                      ))
                    ) : (
                      <p style={{ color: '#666', textAlign: 'center' }}>Aucune donnée disponible</p>
                    )}
                  </div>
                </div>

                {/* Utilisateurs par rôle */}
                <div className="card" style={{ padding: '25px' }}>
                  <h3 style={{ marginBottom: '20px', color: '#333', fontSize: '20px' }}>👥 Utilisateurs par rôle</h3>
                  <div style={{ display: 'grid', gap: '15px' }}>
                    {statistics?.users?.byRole && Object.keys(statistics.users.byRole).length > 0 ? (
                      Object.entries(statistics.users.byRole).map(([role, count]) => (
                        <div key={role} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ color: '#666' }}>{role}:</span>
                          <strong style={{ color: '#FF0000', fontSize: '18px' }}>{count}</strong>
                        </div>
                      ))
                    ) : (
                      <p style={{ color: '#666', textAlign: 'center' }}>Aucune donnée disponible</p>
                    )}
                  </div>
                </div>

                {/* Revenus par mois */}
                <div className="card" style={{ padding: '25px' }}>
                  <h3 style={{ marginBottom: '20px', color: '#333', fontSize: '20px' }}>📈 Revenus par mois (6 derniers mois)</h3>
                  <div style={{ display: 'grid', gap: '15px', maxHeight: '300px', overflowY: 'auto' }}>
                    {statistics?.revenue?.byMonth && statistics.revenue.byMonth.length > 0 ? (
                      statistics.revenue.byMonth.map((item, index) => (
                        <div key={index} style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <strong style={{ color: '#333' }}>{item.month}</strong>
                            <strong style={{ color: '#FF0000' }}>{formatCurrency(item.revenue)}</strong>
                          </div>
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            {item.count} commande(s) • Commission: {formatCurrency(item.commission)}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p style={{ color: '#666', textAlign: 'center' }}>Aucune donnée disponible</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Statistiques de paiement */}
              <div className="card" style={{ padding: '25px', marginBottom: '40px' }}>
                <h3 style={{ marginBottom: '20px', color: '#333', fontSize: '20px' }}>💳 Statistiques de paiement</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                  <div>
                    <p style={{ color: '#666', marginBottom: '5px' }}>Commandes payées:</p>
                    <strong style={{ color: '#28a745', fontSize: '24px' }}>{statistics?.revenue?.paidOrdersCount || 0}</strong>
                  </div>
                  <div>
                    <p style={{ color: '#666', marginBottom: '5px' }}>Valeur moyenne:</p>
                    <strong style={{ color: '#FF0000', fontSize: '24px' }}>
                      {formatCurrency(statistics?.revenue?.averageOrderValue || 0)}
                    </strong>
                  </div>
                  <div>
                    <p style={{ color: '#666', marginBottom: '5px' }}>Frais de livraison:</p>
                    <strong style={{ color: '#0c5460', fontSize: '24px' }}>
                      {formatCurrency(statistics?.revenue?.deliveryFees || 0)}
                    </strong>
                  </div>
                </div>
              </div>
            </div>
              )}
            </>
          )}

          {/* Gestion des utilisateurs */}

          {/* Gestion des utilisateurs */}
          {activeTab === 'users' && (
            <div>
              {/* Filtres */}
              <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                      🔍 Recherche
                    </label>
                    <input
                      type="text"
                      placeholder="Email, nom..."
                      value={userFilters.search}
                      onChange={(e) => handleUserFilterChange('search', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '14px',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                      👤 Rôle
                    </label>
                    <select
                      value={userFilters.role}
                      onChange={(e) => handleUserFilterChange('role', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '14px',
                      }}
                    >
                      <option value="">Tous</option>
                      <option value="client">Client</option>
                      <option value="agent">Agent</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div style={{ marginTop: '15px', textAlign: 'right' }}>
                  <span style={{ color: '#666', fontSize: '14px' }}>
                    Total: <strong>{userPagination.total}</strong> utilisateur(s)
                  </span>
                </div>
              </div>

              {/* Liste des utilisateurs */}
              {users.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ color: '#666' }}>Aucun utilisateur trouvé</p>
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gap: '15px', marginBottom: '20px' }}>
                    {users.map((userItem) => (
                      <div key={userItem._id} className="card" style={{ padding: '20px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
                          <div style={{ flex: 1 }}>
                            <h3 style={{ margin: '0 0 10px 0', color: '#333' }}>
                              {userItem.firstName} {userItem.lastName}
                            </h3>
                            <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                              <strong>Email:</strong> {userItem.email}
                            </p>
                            {userItem.phone && (
                              <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                                <strong>Téléphone:</strong> {userItem.phone}
                              </p>
                            )}
                            <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                              <strong>Rôle:</strong>{' '}
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '4px',
                                backgroundColor: userItem.role === 'admin' ? '#dc3545' : userItem.role === 'agent' ? '#ffc107' : '#28a745',
                                color: 'white',
                                fontSize: '12px',
                                fontWeight: 'bold',
                              }}>
                                {userItem.role}
                              </span>
                            </p>
                            <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                              <strong>Statut:</strong>{' '}
                              <span style={{
                                color: userItem.isActive ? '#28a745' : '#dc3545',
                                fontWeight: 'bold',
                              }}>
                                {userItem.isActive ? '✅ Actif' : '❌ Inactif'}
                              </span>
                            </p>
                            <p style={{ margin: '5px 0', fontSize: '14px', color: '#666' }}>
                              <strong>Commandes:</strong> {userItem.orderCount || 0}
                            </p>
                            <p style={{ margin: '5px 0', fontSize: '12px', color: '#999' }}>
                              Inscrit le: {new Date(userItem.createdAt).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                            <button
                              onClick={() => {
                                setEditingUser(userItem);
                                setNewRole(userItem.role);
                              }}
                              className="btn btn-secondary"
                              style={{ whiteSpace: 'nowrap', fontSize: '14px' }}
                            >
                              ✏️ Modifier le rôle
                            </button>
                            <button
                              onClick={() => handleToggleUserStatus(userItem._id)}
                              className="btn"
                              style={{
                                whiteSpace: 'nowrap',
                                fontSize: '14px',
                                backgroundColor: userItem.isActive ? '#dc3545' : '#28a745',
                                color: 'white',
                              }}
                            >
                              {userItem.isActive ? '🚫 Désactiver' : '✅ Activer'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {userPagination.totalPages > 1 && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: '10px',
                      marginTop: '30px',
                    }}>
                      <button
                        onClick={() => setUserFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={userFilters.page === 1}
                        className="btn btn-secondary"
                        style={{
                          opacity: userFilters.page === 1 ? 0.5 : 1,
                          cursor: userFilters.page === 1 ? 'not-allowed' : 'pointer',
                        }}
                      >
                        ← Précédent
                      </button>
                      <span style={{ color: '#666', fontSize: '14px' }}>
                        Page <strong>{userFilters.page}</strong> sur <strong>{userPagination.totalPages}</strong>
                      </span>
                      <button
                        onClick={() => setUserFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={userFilters.page >= userPagination.totalPages}
                        className="btn btn-secondary"
                        style={{
                          opacity: userFilters.page >= userPagination.totalPages ? 0.5 : 1,
                          cursor: userFilters.page >= userPagination.totalPages ? 'not-allowed' : 'pointer',
                        }}
                      >
                        Suivant →
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Modal pour modifier le rôle */}
              {editingUser && (
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: '20px',
                  }}
                  onClick={() => setEditingUser(null)}
                >
                  <div
                    className="card"
                    style={{
                      maxWidth: '500px',
                      width: '100%',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h2 style={{ marginBottom: '20px', color: '#FF0000' }}>
                      Modifier le rôle de {editingUser.firstName} {editingUser.lastName}
                    </h2>
                    <div className="form-group">
                      <label htmlFor="newRole">Nouveau rôle</label>
                      <select
                        id="newRole"
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          fontSize: '14px',
                        }}
                      >
                        <option value="client">Client</option>
                        <option value="agent">Agent</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                      <button
                        className="btn btn-primary"
                        onClick={() => handleUpdateRole(editingUser._id, newRole)}
                      >
                        Enregistrer
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setEditingUser(null)}
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Statistiques de connexions */}
          {activeTab === 'connections' && (
            <div>
              {/* Filtres */}
              <div className="card" style={{ marginBottom: '20px', padding: '20px' }}>
                <h3 style={{ marginBottom: '20px', color: '#333', fontSize: '20px' }}>
                  🔌 Statistiques de connexions utilisateurs
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                      📅 Période
                    </label>
                    <select
                      value={connectionFilters.period}
                      onChange={(e) => handleConnectionFilterChange('period', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '14px',
                      }}
                    >
                      <option value="day">Aujourd'hui</option>
                      <option value="week">7 derniers jours</option>
                      <option value="month">30 derniers jours</option>
                      <option value="year">12 derniers mois</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                      📆 Date de début
                    </label>
                    <input
                      type="date"
                      value={connectionFilters.startDate}
                      onChange={(e) => handleConnectionFilterChange('startDate', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '14px',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                      📆 Date de fin
                    </label>
                    <input
                      type="date"
                      value={connectionFilters.endDate}
                      onChange={(e) => handleConnectionFilterChange('endDate', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '14px',
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600' }}>
                      👤 Rôle
                    </label>
                    <select
                      value={connectionFilters.role}
                      onChange={(e) => handleConnectionFilterChange('role', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        fontSize: '14px',
                      }}
                    >
                      <option value="">Tous les rôles</option>
                      <option value="client">Client</option>
                      <option value="agent">Agent</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={fetchConnectionStatistics}
                    style={{ fontSize: '14px' }}
                  >
                    🔄 Actualiser
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleExportReport}
                    style={{ fontSize: '14px' }}
                  >
                    📥 Exporter le rapport (CSV)
                  </button>
                </div>
              </div>

              {/* Affichage des statistiques */}
              {connectionLoading ? (
                <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ color: '#666', marginBottom: '20px' }}>
                    Chargement des statistiques de connexions...
                  </p>
                </div>
              ) : !connectionStats ? (
                <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ color: '#dc3545', marginBottom: '20px' }}>
                    Impossible de charger les statistiques de connexions
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={fetchConnectionStatistics}
                  >
                    Réessayer
                  </button>
                </div>
              ) : (
                <div>
                  {/* Cartes de statistiques principales */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                    gap: '20px',
                    marginBottom: '30px',
                  }}>
                    <div className="card" style={{ textAlign: 'center', padding: '25px', backgroundColor: '#d1ecf1' }}>
                      <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#0c5460', marginBottom: '10px' }}>
                        {connectionStats?.statistics?.totalConnections || 0}
                      </div>
                      <div style={{ color: '#666', fontSize: '16px' }}>Total Connexions</div>
                    </div>

                    <div className="card" style={{ textAlign: 'center', padding: '25px', backgroundColor: '#d4edda' }}>
                      <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#155724', marginBottom: '10px' }}>
                        {connectionStats?.statistics?.uniqueUsersCount || 0}
                      </div>
                      <div style={{ color: '#666', fontSize: '16px' }}>Utilisateurs Uniques</div>
                    </div>

                    <div className="card" style={{ textAlign: 'center', padding: '25px', backgroundColor: '#fff3cd' }}>
                      <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#856404', marginBottom: '10px' }}>
                        {formatDuration(connectionStats?.statistics?.averageSessionDuration || 0)}
                      </div>
                      <div style={{ color: '#666', fontSize: '16px' }}>Durée moyenne de session</div>
                    </div>
                  </div>

                  {/* Connexions par rôle */}
                  {connectionStats?.statistics?.connectionsByRole && Object.keys(connectionStats.statistics.connectionsByRole).length > 0 && (
                    <div className="card" style={{ padding: '25px', marginBottom: '30px' }}>
                      <h3 style={{ marginBottom: '20px', color: '#333', fontSize: '20px' }}>👥 Connexions par rôle</h3>
                      <div style={{ display: 'grid', gap: '15px' }}>
                        {Object.entries(connectionStats.statistics.connectionsByRole).map(([role, data]) => (
                          <div key={role} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                            <span style={{ color: '#666', fontWeight: '600' }}>{role}:</span>
                            <div style={{ textAlign: 'right' }}>
                              <strong style={{ color: '#FF0000', fontSize: '18px' }}>{data.count} connexions</strong>
                              <div style={{ fontSize: '12px', color: '#999' }}>{data.uniqueUsers} utilisateur(s) unique(s)</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Connexions par jour */}
                  {connectionStats?.statistics?.connectionsByDay && connectionStats.statistics.connectionsByDay.length > 0 && (
                    <div className="card" style={{ padding: '25px', marginBottom: '30px' }}>
                      <h3 style={{ marginBottom: '20px', color: '#333', fontSize: '20px' }}>📈 Évolution quotidienne</h3>
                      <div style={{ display: 'grid', gap: '10px', maxHeight: '400px', overflowY: 'auto' }}>
                        {connectionStats.statistics.connectionsByDay.map((item, index) => (
                          <div key={index} style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <strong style={{ color: '#333' }}>
                                {new Date(item.date).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                              </strong>
                              <div style={{ textAlign: 'right' }}>
                                <strong style={{ color: '#FF0000', fontSize: '18px' }}>{item.count} connexions</strong>
                                <div style={{ fontSize: '12px', color: '#999' }}>{item.uniqueUsers} utilisateur(s) unique(s)</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Top utilisateurs */}
                  {connectionStats?.statistics?.topUsers && connectionStats.statistics.topUsers.length > 0 && (
                    <div className="card" style={{ padding: '25px', marginBottom: '30px' }}>
                      <h3 style={{ marginBottom: '20px', color: '#333', fontSize: '20px' }}>🏆 Top 10 utilisateurs les plus actifs</h3>
                      <div style={{ display: 'grid', gap: '10px' }}>
                        {connectionStats.statistics.topUsers.map((userItem, index) => (
                          <div key={index} style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <strong style={{ color: '#333' }}>
                                  {index + 1}. {userItem.firstName} {userItem.lastName}
                                </strong>
                                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                  {userItem.email} • {userItem.role}
                                </div>
                                <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                                  Dernière connexion: {new Date(userItem.lastConnection).toLocaleString('fr-FR')}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <strong style={{ color: '#FF0000', fontSize: '24px' }}>{userItem.connectionCount}</strong>
                                <div style={{ fontSize: '12px', color: '#999' }}>connexions</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Connexions par heure */}
                  {connectionStats?.statistics?.connectionsByHour && connectionStats.statistics.connectionsByHour.length > 0 && (
                    <div className="card" style={{ padding: '25px' }}>
                      <h3 style={{ marginBottom: '20px', color: '#333', fontSize: '20px' }}>⏰ Répartition par heure de la journée</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '10px' }}>
                        {connectionStats.statistics.connectionsByHour.map((item, index) => (
                          <div key={index} style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>{item.hour}h</div>
                            <strong style={{ color: '#FF0000', fontSize: '18px' }}>{item.count}</strong>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminDashboard;

